import { shouldShowSeparateMiscAccountGuide } from '../data/stateLoginPortalMap'

export type ServiceType = 'SurplusLines' | 'Licensing' | 'Both'

export type FilingsCurrentStatus = '' | 'yes' | 'no'

export type StepId =
  | 'welcome'
  | 'checklist'
  | 'important'
  | 'generalQuestions'
  | 'attestation'
  | 'businessLicenses'
  | 'individualLicenses'
  | 'drlpAssignment'
  | 'logins'
  | 'separateMiscAccountGuide'
  | 'newLoginWizard'
  | 'idahoAgreement'
  | 'texasAdminAccountAccess'
  | 'oregonNorthCarolinaUserAccountAccess'
  | 'newYorkAdminAccountAccess'
  | 'floridaGeorgiaMississippiUserAccountAccess'
  | 'maineTaxPortalAccess'
  | 'rhodeIslandTaxPortalAccess'
  | 'vermontMyVaxAccess'
  | 'newMexicoTapAccess'
  | 'contacts'
  | 'bor'
  | 'otr'
  | 'applications'
  | 'training'
  | 'complete'

export type AgreementStatus = 'pending' | 'signed'

export type BorStateStatus =
  | 'not_started'
  | 'generated'
  | 'uploaded'
  | 'misc_reviewed'

export interface ContactPerson {
  name: string
  title: string
  phone: string
  email: string
}

/** Contact row on the onboarding wizard (role + person fields). */
export interface OnboardingContact {
  id: string
  role: string
  firstName: string
  lastName: string
  phone: string
  email: string
}

export function isOnboardingContactComplete(c: OnboardingContact): boolean {
  return [c.role, c.firstName, c.lastName, c.phone, c.email].every((s) => String(s).trim().length > 0)
}

export interface LicenseRow {
  licenseeId: string
  licenseeFirstName: string
  licenseeLastName: string
  npn: string
  entity: 'Individual' | 'BusinessEntity'
  fein: string | null
  licenseState: string
  licenseNumber: string
  licenseType: string
  residentState: boolean
  /** Demo: include in zero-report obligations without treating as a full resident license. */
  zeroReportOnly?: boolean
  status: 'Active' | 'Expired' | 'Pending'
  expirationDate: string
  source: 'NIPR_PDB' | 'Manual' | 'AgentSync'
  clientConfirmed: boolean | null
  flaggedForReview: boolean
  /** At most one DRLP per license state; multiple states ⇒ multiple DRLPs. */
  isDrlp: boolean
  /** Associated business entity (individual licensees). */
  companyName?: string
}

export function stepIndexForId(steps: StepId[], stepId: StepId): number {
  const idx = steps.indexOf(stepId)
  return idx === -1 ? 0 : idx
}

/** Display label for tables; business entities prefer a single legal name in last name. */
export function licenseDisplayName(row: LicenseRow): string {
  if (row.entity === 'BusinessEntity') {
    const combined = `${row.licenseeFirstName} ${row.licenseeLastName}`.trim()
    return combined || row.licenseeLastName.trim() || '(Unnamed)'
  }
  const s = `${row.licenseeFirstName} ${row.licenseeLastName}`.trim()
  return s || '(Unnamed)'
}

/** Editable name field value — empty when unset so placeholder can show; does not trim (spaces stay). */
export function licenseNameInputValue(row: LicenseRow): string {
  const first = row.licenseeFirstName
  const last = row.licenseeLastName
  if (first && last) return `${first} ${last}`
  return first || last
}

/** Illinois SL Logins: individual logins vs corporate multi-licensee account. */
export type IllinoisLoginMode = 'unset' | 'individual' | 'corporate'

export interface StateLoginRow {
  /** Stable row id from `STATE_LOGIN_PORTAL_ROWS` (e.g. `CA-SLIP`, `OPTINS`). */
  stateCode: string
  /** Two-letter code for licensee defaults; null for OPTins / SLIP+ platform rows. */
  licenseJurisdictionCode: string | null
  portalUrl: string
  miscManaged: boolean
  username: string
  password: string
  complete: boolean
  /** Editable display name for the Licensee column (defaults from license data when row is created). */
  licenseeDisplay: string
  /** User defers credential entry; row is grayed on SL Logins and appears on New Login Wizard. */
  pendingLoginWizard: boolean
  /**
   * Business entity this login is for — same option list as Company Name on Individual Licenses.
   * When multiple entities exist, user must choose; when exactly one exists, it is defaulted.
   */
  clientCompanyName: string
  /**
   * Illinois individual-mode rows: Individual Licenses `licenseeId` this login belongs to.
   * Absent on corporate / chooser / non-IL rows.
   */
  illinoisSourceLicenseId?: string
  /** True while Illinois is an active business state and the user has not chosen individual vs corporate. */
  illinoisPendingChoice?: boolean
}

export function hasPendingLoginWizardRows(logins: StateLoginRow[]): boolean {
  return logins.some((r) => r.pendingLoginWizard)
}

export interface BorLetterRow {
  stateCode: string
  status: BorStateStatus
}

/** Mock weekly OTR extraction row (client preview + corrections). */
export interface OtrTransactionRow {
  id: string
  invoiceNumber: string
  insuredName: string
  policyNumber: string
  homeState: string
  grossPremium: number
  taxAmt: number
  carrier: string
  flaggedForReview: boolean
}

export interface ProducerAppRow {
  npn: string
  name: string
  appStatus: 'not_started' | 'generated' | 'sent' | 'signed'
}

export interface PipelineClient {
  clientId: string
  clientName: string
  serviceType: ServiceType
  stepsComplete: number
  stepsTotal: number
  transitionDate: string | null
  lastActivity: string
  status: 'NotStarted' | 'InProgress' | 'Complete' | 'Blocked'
  overdue?: boolean
  anomaly?: string
}

/** Surplus Lines tax onboarding flow (service selection and agreement steps removed). */
export function getStepsForService(_serviceType: ServiceType | null): StepId[] {
  void _serviceType
  return [
    'welcome',
    'checklist',
    'important',
    'generalQuestions',
    'attestation',
    'businessLicenses',
    'individualLicenses',
    'drlpAssignment',
    'logins',
    'separateMiscAccountGuide',
    'newLoginWizard',
    'idahoAgreement',
    'texasAdminAccountAccess',
    'oregonNorthCarolinaUserAccountAccess',
    'newYorkAdminAccountAccess',
    'floridaGeorgiaMississippiUserAccountAccess',
    'maineTaxPortalAccess',
    'rhodeIslandTaxPortalAccess',
    'vermontMyVaxAccess',
    'newMexicoTapAccess',
    'contacts',
    'bor',
    'otr',
    'training',
    'complete',
  ]
}

/** Full step list with conditional steps omitted when they do not apply. */
export function getEffectiveStepIds(
  serviceType: ServiceType | null,
  stateLogins: StateLoginRow[],
  activeBusinessStates: string[],
): StepId[] {
  const all = getStepsForService(serviceType)
  let ids = [...all]
  ids = ids.filter((id) => id !== 'bor')
  if (!hasPendingLoginWizardRows(stateLogins)) {
    ids = ids.filter((id) => id !== 'newLoginWizard')
  }
  if (!shouldShowSeparateMiscAccountGuide(stateLogins, activeBusinessStates)) {
    ids = ids.filter((id) => id !== 'separateMiscAccountGuide')
  }
  if (!shouldShowIdahoAgreement(activeBusinessStates)) {
    ids = ids.filter((id) => id !== 'idahoAgreement')
  }
  if (!shouldShowTexasAdminAccountAccess(activeBusinessStates)) {
    ids = ids.filter((id) => id !== 'texasAdminAccountAccess')
  }
  if (!shouldShowOregonNorthCarolinaUserAccountAccess(activeBusinessStates)) {
    ids = ids.filter((id) => id !== 'oregonNorthCarolinaUserAccountAccess')
  }
  if (!shouldShowNewYorkAdminAccountAccess(activeBusinessStates)) {
    ids = ids.filter((id) => id !== 'newYorkAdminAccountAccess')
  }
  if (!shouldShowFloridaGeorgiaMississippiUserAccountAccess(activeBusinessStates)) {
    ids = ids.filter((id) => id !== 'floridaGeorgiaMississippiUserAccountAccess')
  }
  if (!shouldShowMaineTaxPortalAccess(activeBusinessStates)) {
    ids = ids.filter((id) => id !== 'maineTaxPortalAccess')
  }
  if (!shouldShowRhodeIslandTaxPortalAccess(activeBusinessStates)) {
    ids = ids.filter((id) => id !== 'rhodeIslandTaxPortalAccess')
  }
  if (!shouldShowVermontMyVaxAccess(activeBusinessStates)) {
    ids = ids.filter((id) => id !== 'vermontMyVaxAccess')
  }
  if (!shouldShowNewMexicoTapAccess(activeBusinessStates)) {
    ids = ids.filter((id) => id !== 'newMexicoTapAccess')
  }
  return ids
}

export function shouldShowIdahoAgreement(activeBusinessStates: readonly string[]): boolean {
  return activeBusinessStates.some((code) => code.toUpperCase() === 'ID')
}

export function shouldShowTexasAdminAccountAccess(activeBusinessStates: readonly string[]): boolean {
  return activeBusinessStates.some((code) => code.toUpperCase() === 'TX')
}

export function shouldShowOregonNorthCarolinaUserAccountAccess(
  activeBusinessStates: readonly string[],
): boolean {
  return activeBusinessStates.some((code) => ['OR', 'NC'].includes(code.toUpperCase()))
}

export function shouldShowNewYorkAdminAccountAccess(activeBusinessStates: readonly string[]): boolean {
  return activeBusinessStates.some((code) => code.toUpperCase() === 'NY')
}

export function shouldShowFloridaGeorgiaMississippiUserAccountAccess(
  activeBusinessStates: readonly string[],
): boolean {
  return activeBusinessStates.some((code) => ['FL', 'GA', 'MS'].includes(code.toUpperCase()))
}

export function shouldShowMaineTaxPortalAccess(activeBusinessStates: readonly string[]): boolean {
  return activeBusinessStates.some((code) => code.toUpperCase() === 'ME')
}

export function shouldShowRhodeIslandTaxPortalAccess(
  activeBusinessStates: readonly string[],
): boolean {
  return activeBusinessStates.some((code) => code.toUpperCase() === 'RI')
}

export function shouldShowVermontMyVaxAccess(activeBusinessStates: readonly string[]): boolean {
  return activeBusinessStates.some((code) => code.toUpperCase() === 'VT')
}

export function shouldShowNewMexicoTapAccess(activeBusinessStates: readonly string[]): boolean {
  return activeBusinessStates.some((code) => code.toUpperCase() === 'NM')
}

export function nextVisibleStepIndex(
  serviceType: ServiceType | null,
  stateLogins: StateLoginRow[],
  activeBusinessStates: string[],
  currentStepIndex: number,
): number | null {
  const full = getStepsForService(serviceType)
  const eff = getEffectiveStepIds(serviceType, stateLogins, activeBusinessStates)
  const safeIdx = Math.min(Math.max(0, currentStepIndex), Math.max(0, full.length - 1))
  for (let i = safeIdx + 1; i < full.length; i++) {
    if (eff.includes(full[i])) return i
  }
  return null
}

export function prevVisibleStepIndex(
  serviceType: ServiceType | null,
  stateLogins: StateLoginRow[],
  activeBusinessStates: string[],
  currentStepIndex: number,
): number | null {
  const full = getStepsForService(serviceType)
  const eff = getEffectiveStepIds(serviceType, stateLogins, activeBusinessStates)
  const safeIdx = Math.min(Math.max(0, currentStepIndex), Math.max(0, full.length - 1))
  for (let i = safeIdx - 1; i >= 0; i--) {
    if (eff.includes(full[i])) return i
  }
  return null
}
